<template>
    <modal name="automation-center"
           height="98%"
           width="95%"
           :pivotY="0"
           @before-open="beforeOpen"
           @before-close="beforeClose"
           :clickToClose="false"
           classes="bg-transparent text-left">
        <div class="row my-2 py-1">
            <div class="col-6 offset-6 text-right">
                <div @click="closeModal"
                     v-tooltip.top="'Close modal'"
                     class="modal-toolbar-btn close-modal-btn">
                    <i class="far fa-times fa-lg"></i>
                </div>
            </div>
        </div>

        <AutomationDataProvider class="container-fluid rounded modal-content" v-slot="{ isDataLoading }">
            <div v-if="isDataLoading || isLoading" class="text-muted font-20 h-100 w-100 text-center mt-5">
                Loading...
            </div>
            <template v-else>
                <div v-if="selectedAutomation" class="h-100">
                    <AutomationBuilder
                        :tab="selectedAutomationTab"
                        :automationId="selectedAutomation.uuid"
                        @close="selectedAutomation = null"
                        @tab-update="onAutomationBuilderTabUpdate"
                        :evalOrder="allAutomationsCount"
                        :isTemplate="activeTabKey === tabs.library.key">
                        <div slot="header" class="py-3 px-4">
                            <span @click="onAutomationSelect(null)" slot="header-left" class="back-btn">
                                <i class="fal fa-chevron-left"></i>
                                <span class="pl-2">Back to {{ isMarketplace ? 'Marketplace' : entityNamePlural }}</span>
                            </span>
                        </div>
                    </AutomationBuilder>
                </div>
                <div v-show="!selectedAutomation" class="h-100">
                    <div class="text-center border-bottom m-0">
                        <div v-for="tab in tabs" :key="tab.key"
                             @click="activeTabKey = tab.key"
                             class="automation-tab-btn"
                             :class="{ active: activeTabKey === tab.key }">
                            {{ tab.label }}
                            <span v-if="tab.key === tabs.board.key && stats.enabled !== 0">
                                / {{ stats.enabled }}
                            </span>
                        </div>
                    </div>
                    <div class="content-container">
                        <AutomationLibrary
                            v-if="activeTabKey === tabs.library.key"
                            @select="onAutomationSelect"
                            :templates="templates"
                            :type="type">
                            <template v-slot:footer>
                                <AutomationCreateCustomBanner
                                    v-if="!isMarketplace"
                                    @create="onAutomationSelect"
                                    :type="type"
                                    id="template-group-custom"
                                    class="mt-4 mr-2"/>
                            </template>
                        </AutomationLibrary>
                        <AutomationBoard
                            v-else-if="activeTabKey === tabs.board.key"
                            :type="type"
                            @select="onAutomationSelect"
                            @created="refreshAutomationStats"
                            @updated="refreshAutomationStats"
                            @deleted="refreshAutomationStats"
                            :loading="isApplyingBoardFilter"
                            class="h-100 px-5 pb-3">
                            <template v-slot:create-btn>
                                <button @click="activeTabKey = tabs.library.key" class="btn btn-sm btn-primary">
                                    Create {{ isMarketplace ? 'automation' : entityName }}
                                </button>
                            </template>
                        </AutomationBoard>
                    </div>
                </div>
            </template>
        </AutomationDataProvider>
    </modal>
</template>

<script>
import AutomationBoard from './board/AutomationBoard'
import AutomationBuilder from './builder/AutomationBuilder'
import AutomationLibrary from './library/AutomationLibrary'
import AutomationCreateCustomBanner from './library/AutomationCreateCustomBanner'
import AutomationDataProvider from './data-provider/AutomationDataProvider'
import {AutomationBuilderTabs, getAutomationBuilderDefaultTab} from './builder'
import {AutomationTypes, getAutomationTypeBaseModelById} from '@/types/automations/types'
import {automationsApi} from '@/api/automations'
import {AppDocSystemTypes} from '@/types/document/types'
import {appHelpChat} from '@/services/chats'

const tabKeys = {
    LIBRARY: 'LIBRARY',
    BOARD: 'BOARD'
}

export default {
    name: 'AutomationCenter',
    components: {
        AutomationDataProvider,
        AutomationCreateCustomBanner,
        AutomationLibrary,
        AutomationBuilder,
        AutomationBoard
    },
    data() {
        return {
            type: AutomationTypes.AUTOMATION,
            activeTabKey: tabKeys.LIBRARY,
            templates: [],
            stats: {
                enabled: 0,
                disabled: 0
            },
            isLoading: false,
            isApplyingBoardFilter: false,
            selectedAutomation: null,
            selectedAutomationTab: getAutomationBuilderDefaultTab(),
            showFilter: false
        }
    },
    computed: {
        isIntegrationsCenter() {
            return this.type === AutomationTypes.INTEGRATION
        },

        isMarketplace() {
            return this.type === AutomationTypes.MARKETPLACE
        },

        tabs() {
            return {
                library: {
                    key: tabKeys.LIBRARY,
                    label: this.isMarketplace ? this.entityName : `${this.entityNamePlural} center`
                },
                board: {
                    key: tabKeys.BOARD,
                    label: this.isMarketplace ? 'Board items' : `Board ${this.entityNamePlural}`
                }
            }
        },

        entityName() {
            return getAutomationTypeBaseModelById(this.type).name
        },

        entityNamePlural() {
            return getAutomationTypeBaseModelById(this.type).name + 's'
        },

        allAutomationsCount() {
            return this.stats.enabled + this.stats.disabled
        }
    },
    methods: {
        closeModal() {
            this.$modal.hide('automation-center')
        },

        async beforeOpen({params}) {
            if (Object.values(AutomationTypes).includes(params.type)) {
                this.type = params.type
            }
            appHelpChat.hide()
            this.isLoading = true
            await this.setUrl()
            await Promise.all([
                this.refreshAutomationStats(),
                this.fetchTemplates()
            ])
            this.isLoading = false

            this.$bus.$on('edit-automation-connection', ({automation, authParamPath}) => {
                this.onAutomationBuilderTabUpdate({
                    key: AutomationBuilderTabs.CONNECTION,
                    params: {
                        path: authParamPath
                    }
                })
                this.selectedAutomation = automation
            })

            if (params.uuid) {
                const automation = await this.fetchAutomationById(params.uuid)
                if (automation) {
                    this.selectedAutomation = automation
                }
            }
        },

        beforeClose() {
            this.clearUrl()
            appHelpChat.show()
            Object.assign(this.$data, this.$options.data.apply(this))
            this.$bus.$off('edit-automation-connection')
        },

        async setUrl() {
            const typeBaseModel = getAutomationTypeBaseModelById(this.type)
            const query = {...this.$route.query, automationType: typeBaseModel.uuid}
            await this.$router.replace({query}).catch(() => {
            })
        },

        async clearUrl() {
            const query = {...this.$route.query}
            delete query.automationType
            await this.$router.replace({query}).catch(() => {
            })
        },

        onAutomationBuilderTabUpdate(tab) {
            this.selectedAutomationTab = tab
        },

        onAutomationSelect(automation) {
            this.selectedAutomation = automation
            this.selectedAutomationTab = getAutomationBuilderDefaultTab({
                type: this.type,
                isTemplate: Boolean(automation && automation.isTemplate)
            })
        },

        async refreshAutomationStats() {
            await Promise.all([
                this.fetchEnabledAutomationsCount(),
                this.fetchDisabledAutomationsCount()
            ])
        },

        async fetchEnabledAutomationsCount() {
            const {data} = await automationsApi.getCount({
                section: AppDocSystemTypes.PURCHASING,
                isTemplate: false,
                enabled: true
            })
            this.stats.enabled = data || 0
        },

        async fetchDisabledAutomationsCount() {
            const {data} = await automationsApi.getCount({
                section: AppDocSystemTypes.PURCHASING,
                isTemplate: false,
                enabled: false
            })
            this.stats.disabled = data || 0
        },

        async fetchAutomationById(id) {
            const {data, error} = await automationsApi.fetch(id)
            if (error) {
                return null
            }
            return data
        },

        async fetchTemplates() {
            this.isLoading = true
            const {data} = await automationsApi.fetchAll({
                section: AppDocSystemTypes.PURCHASING,
                isTemplate: true
            })
            this.templates = data
            this.isLoading = false
        }
    }
}
</script>

<style scoped lang="scss">
.modal-content {
    height: calc(100% - 80px);
    padding: 0;
    background: white;
    overflow-y: scroll;
    @include media-breakpoint-down(sm) {
    }
}

.content-container {
    height: calc(100% - 68px);
    overflow: hidden;
}

.automation-tab-btn {
    display: inline-block;
    font-size: 18px;
    padding: 20px;

    &:hover {
        cursor: pointer;
    }

    &.active {
        font-weight: 500;
        border-bottom: 3px solid $color-purple-light;
        color: $color-purple-light;
    }
}

.back-btn {
    font-size: 16px;

    &:hover {
        cursor: pointer;
        color: $color-purple-light;
    }
}
</style>
